import { useQueries, useQueryClient } from '@tanstack/react-query'
import { onValue } from 'firebase/database'
import { useEffect, useMemo } from 'react'

import {
  useNotesServiceGetV1IndividualsByIndividualIdNotesByNoteIdKey,
  useNotesServiceGetV1IndividualsByIndividualIdNotesKey,
} from '@/api/client/queries/common'
import { getRealtimeRef } from '@/integrations/firebase'

export type NoteGenerationStatus = 'idle' | 'generating' | 'done' | 'error'

/**
 * Subscribes to RTDB note-generation progress at
 * individuals/{individualId}/notes/{noteId}. Mirrors useDocumentUploadStatus:
 * each note gets a disabled query that an onValue listener populates, so reads
 * stay reactive without polling. Returns a { [noteId]: status } map.
 */
export const useNoteGenerationStatus = (individualId: string, noteIds: string[]) => {
  const results = useQueries({
    queries: noteIds.map(noteId => ({
      enabled: false,
      queryFn: () => Promise.resolve({ status: 'idle' as NoteGenerationStatus }),
      queryKey: ['noteGenerationStatus', individualId, noteId],
    })),
  })
  const queryClient = useQueryClient()

  useEffect(() => {
    const unsubscribes = noteIds.map(noteId => {
      const dbRef = getRealtimeRef(`individuals/${individualId}/notes/${noteId}`)

      return onValue(dbRef, snapshot => {
        if (!snapshot.exists()) return

        const data = snapshot.val()
        const previous = queryClient.getQueryData<{ status: NoteGenerationStatus }>([
          'noteGenerationStatus',
          individualId,
          noteId,
        ])
        queryClient.setQueryData(['noteGenerationStatus', individualId, noteId], data)

        // When generation settles, the note's title/events/eventCount changed
        // server-side — refetch the API-backed queries so the UI catches up.
        const settled = data.status === 'done' || data.status === 'error'

        if (settled && previous?.status !== data.status) {
          queryClient.invalidateQueries({
            queryKey: [useNotesServiceGetV1IndividualsByIndividualIdNotesKey],
          })
          queryClient.invalidateQueries({
            queryKey: [useNotesServiceGetV1IndividualsByIndividualIdNotesByNoteIdKey],
          })
        }
      })
    })

    return () => {
      unsubscribes.forEach(unsubscribe => unsubscribe())
    }
  }, [individualId, noteIds, queryClient])

  const statuses = useMemo(() => {
    return results.reduce<Record<string, NoteGenerationStatus>>((acc, curr, i) => {
      if (curr.data) {
        const id = noteIds[i] ?? ''
        acc[id] = curr.data.status
      }

      return acc
    }, {})
  }, [results])

  return statuses
}
